import React, { Suspense } from 'react';
import { Box, Breadcrumbs, Button, Grid, Link, Typography, withStyles } from '@material-ui/core';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import useManageProgramsHook from './useManageProgramsHook';
import styles from './manage-programs.style';
import AddIcon from '@mui/icons-material/Add';
import { withRouter } from '../../../util/router-compatibility';
import { DataGridWithHeaders, Loader } from '@orijinworks/frontend-commons';
import AddProgram from './components/add-program/add-program';
import ProgramList from './components/program-list/program-list';

const ManagePrograms = ({ classes }) => {
  const {
    programs,
    programData,
    loading,
    showAddProgram,
    showEditProgram,
    handleAddNew,
    handleBackToList,
    handleEditProgram,
    formatLastRefreshedTime,
    lastRefreshed,
    columns,
    pageSize,
    refreshPrograms,
    getProgramAndPopulateEditFields,
    shouldShowManageProgramsNewUI
  } = useManageProgramsHook();


  const renderProgramList = () => (
    shouldShowManageProgramsNewUI
      ? renderNewProgramList()
      : renderLegacyProgramList()
  );

  const renderLegacyProgramList = () => {
    return (
      <Suspense fallback={<Loader isLoading={true} />}>
        <Grid container item component="div" className={classes.content}>
          <Grid container spacing={3}>
            <Grid item container>
              <Loader isLoading={loading} />
            </Grid>

            {!showAddProgram ? (
              <>
                <Grid container item>
                  <Typography variant="h1" color="secondary" component="h1">
                    Program Library
                  </Typography>
                </Grid>
                <Grid container item justify="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddNew()}
                    className={classes.addButton}
                  >
                    Add New Program
                  </Button>
                </Grid>
                <ProgramList
                  programs={programs}
                  getProgramAndPopulateEditFields={getProgramAndPopulateEditFields}
                  classes={classes}
                />
              </>
            ) : (
              <>
                <Grid container item>
                  <Breadcrumbs
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                  >
                    <Link
                      component="button"
                      variant="body1"
                      onClick={handleBackToList}
                      className={classes.breadcrumbLink}
                    >
                      Program Library
                    </Link>
                    <Typography
                      variant="body1"
                      className={classes.breadcrumbCurrent}
                    >
                      {showEditProgram ? 'Edit Program' : 'Add New Program'}
                    </Typography>
                  </Breadcrumbs>
                </Grid>
                <AddProgram
                  handleAddNew={handleAddNew}
                  handleBackToList={handleBackToList}
                  isEditingMode={showEditProgram}
                  programData={programData}
                  handleEditProgram={handleEditProgram}
                />
              </>
            )}
          </Grid>
        </Grid>
      </Suspense>
    );
  };

  const renderNewProgramList = () => (
    <Box className={classes.gridContainer}>
      <DataGridWithHeaders
        title="Program Library"
        subtitle={formatLastRefreshedTime(lastRefreshed)}
        dataGridProps={{
          columns,
          data: programs,
          pageSize,
          pageSizeOptions: [20, 50, 100],
          paginationMode: 'client',
          isLoading: loading,
          showCheckbox: false,
          onRefreshHandler: refreshPrograms,
          primaryButtonProps: {
            text: 'Add New Program',
            variant: 'contained',
            startIcon: <AddIcon />,
            onClick: handleAddNew,
          },
          hasActionColumn: false,
          tdStyles: classes.tableCellStyles,
        }}
      />
    </Box>
  );

  const renderAddEditProgram = () => (
    <>
      <Grid container item>
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            component="button"
            variant="body1"
            onClick={handleBackToList}
            className={classes.breadcrumbLink}
          >
            Program Library
          </Link>
          <Typography
            variant="body1"
            className={classes.breadcrumbCurrent}
          >
            {showEditProgram ? 'Edit Program' : 'Add New Program'}
          </Typography>
        </Breadcrumbs>
      </Grid>
      <AddProgram 
        handleAddNew={handleAddNew}
        handleBackToList={handleBackToList} 
        isEditingMode={showEditProgram} 
        programData={programData} 
        handleEditProgram={handleEditProgram}
      />
    </>
  );

  return (
    <Suspense fallback={<Loader isLoading={true} />}>
      <Grid 
        container 
        item 
        component="div" 
        className={shouldShowManageProgramsNewUI ? classes.newGridContent : classes.content}
      >
        <Grid container spacing={3}>
          <Grid item container>
            <Loader isLoading={loading} />
          </Grid>
          {!showAddProgram ? renderProgramList() : renderAddEditProgram()}
        </Grid>
      </Grid>
    </Suspense>
  );
};

export default withStyles(styles)(withRouter(ManagePrograms));
